import { useCallback, useEffect, useState } from "react"
import type { GrantRole, ISharePanelHook, OrgUser, ShareGrantOutcome, ShareTeam } from "./types"

const OFFLINE_PREVIEW_MUTATION_DELAY_MS = 600

interface IShareGrantsConfig {
  initialSharedUsers: OrgUser[] | undefined
  initialSharedTeams: ShareTeam[] | undefined
  onAddUser: ISharePanelHook["onAddUser"] | undefined
  onRemoveUser: ISharePanelHook["onRemoveUser"] | undefined
  onAddTeam: ISharePanelHook["onAddTeam"] | undefined
  onRemoveTeam: ISharePanelHook["onRemoveTeam"] | undefined
}

interface IGranteeListConfig<Grantee extends { role?: GrantRole }> {
  initialGrantees: Grantee[] | undefined
  granteeIdOf: (grantee: Grantee) => string
  onAddGrantee: ((grantee: Grantee, role: GrantRole) => Promise<ShareGrantOutcome>) | undefined
  onRemoveGrantee: ((grantee: Grantee) => Promise<void>) | undefined
  granteeKind: "user" | "team"
  setError: (message: string | null) => void
}

function withoutGranteeId(granteeIds: Set<string>, granteeId: string) {
  const next = new Set(granteeIds)
  next.delete(granteeId)
  return next
}

function waitForOfflinePreviewMutation() {
  return new Promise(resolve => setTimeout(resolve, OFFLINE_PREVIEW_MUTATION_DELAY_MS))
}

function useGranteeList<Grantee extends { role?: GrantRole }>({
  initialGrantees,
  granteeIdOf,
  onAddGrantee,
  onRemoveGrantee,
  granteeKind,
  setError,
}: IGranteeListConfig<Grantee>) {
  const [grantees, setGrantees] = useState<Grantee[]>(initialGrantees ?? [])
  const [addingIds, setAddingIds] = useState<Set<string>>(new Set())
  const [removingIds, setRemovingIds] = useState<Set<string>>(new Set())

  useEffect(() => { setGrantees(initialGrantees ?? []) }, [JSON.stringify(initialGrantees)])

  const add = useCallback(async (grantee: Grantee, role: GrantRole): Promise<boolean> => {
    const granteeId = granteeIdOf(grantee)
    setAddingIds(prev => new Set(prev).add(granteeId))
    setError(null)
    try {
      if (onAddGrantee) {
        await onAddGrantee(grantee, role)
      } else {
        await waitForOfflinePreviewMutation()
      }
      const grantedGrantee = { ...grantee, role }
      setGrantees(prev =>
        prev.some(existing => granteeIdOf(existing) === granteeId)
          ? prev.map(existing => granteeIdOf(existing) === granteeId ? grantedGrantee : existing)
          : [...prev, grantedGrantee]
      )
      return true
    } catch (err) {
      setError((err as Error)?.message || `Failed to add ${granteeKind}`)
      return false
    } finally {
      setAddingIds(prev => withoutGranteeId(prev, granteeId))
    }
  }, [granteeIdOf, onAddGrantee, granteeKind, setError])

  const remove = useCallback(async (grantee: Grantee): Promise<boolean> => {
    const granteeId = granteeIdOf(grantee)
    setRemovingIds(prev => new Set(prev).add(granteeId))
    setError(null)
    try {
      if (onRemoveGrantee) {
        await onRemoveGrantee(grantee)
      } else {
        await waitForOfflinePreviewMutation()
      }
      setGrantees(prev => prev.filter(existing => granteeIdOf(existing) !== granteeId))
      return true
    } catch (err) {
      setError((err as Error)?.message || `Failed to remove ${granteeKind}`)
      return false
    } finally {
      setRemovingIds(prev => withoutGranteeId(prev, granteeId))
    }
  }, [granteeIdOf, onRemoveGrantee, granteeKind, setError])

  return { grantees, addingIds, removingIds, add, remove }
}

const userIdOf = (user: OrgUser) => user.userId
const teamIdOf = (team: ShareTeam) => team.teamId

export function useShareGrants({ initialSharedUsers, initialSharedTeams, onAddUser, onRemoveUser, onAddTeam, onRemoveTeam }: IShareGrantsConfig) {
  const [error, setError] = useState<string | null>(null)
  const clearError = useCallback(() => setError(null), [])

  const users = useGranteeList({
    initialGrantees: initialSharedUsers,
    granteeIdOf: userIdOf,
    onAddGrantee: onAddUser,
    onRemoveGrantee: onRemoveUser,
    granteeKind: "user",
    setError,
  })
  const teams = useGranteeList({
    initialGrantees: initialSharedTeams,
    granteeIdOf: teamIdOf,
    onAddGrantee: onAddTeam,
    onRemoveGrantee: onRemoveTeam,
    granteeKind: "team",
    setError,
  })

  return {
    sharedUsers: users.grantees,
    sharedTeams: teams.grantees,
    error,
    clearError,
    addingUserIds: users.addingIds,
    removingUserIds: users.removingIds,
    addingTeamIds: teams.addingIds,
    removingTeamIds: teams.removingIds,
    addUser: users.add,
    removeUser: users.remove,
    addTeam: teams.add,
    removeTeam: teams.remove,
  }
}
